import React, {useCallback, useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";
import {useWardrobe} from "../context/WardrobeContext";
import {ChevronDownIcon, LightbulbIcon} from './icons';

interface HealthComponent {
    key: string;
    labelRu: string;
    score: number | null;
    weight: number;
    hint?: string | null;
    notComputedReason?: string | null;
}

// Подметрика без данных приходит null (не 100): утилизация — при нехватке истории носки,
// дубли — пока есть вещи без индекса. Подписи и формула — с бэка.
interface WardrobeHealth {
    score: number;
    utilizationScore: number | null;
    coverageScore: number;
    duplicatesScore: number | null;
    completenessScore: number;
    totalItems: number;
    topSuggestion: string;
    /** Машинный код следующего шага; подпись и маршрут кнопки подбираются здесь. */
    topSuggestionCode?: string | null;
    formulaLabel?: string | null;
    components?: HealthComponent[];
    insufficientHistory?: boolean;
    duplicatesUnindexedCount?: number;
    thresholdDays?: number;
    /** Вещей без единой отметки носки — причина, а не «таймер». */
    itemsWithoutWearRecords?: number;
    /** Остаток дней без новых отметок; null — истории достаточно. */
    historyDaysRemaining?: number | null;
    computedAt?: string | null;
    consentRequired?: boolean;
}

const scoreColor = (score: number): string => {
    if (score >= 80) return 'text-emerald-600 dark:text-emerald-400';
    if (score >= 50) return 'text-amber-600 dark:text-amber-400';
    return 'text-rose-600 dark:text-rose-400';
};

// F11: «Утилизация» читается и как «использование гардероба», и как «избавление от одежды».
// Подписи берутся с бэка (components[].labelRu), здесь — запасной вариант для старых ответов.
const SUBS: Array<{key: keyof WardrobeHealth; label: string}> = [
    {key: 'utilizationScore', label: 'Использование гардероба'},
    {key: 'coverageScore', label: 'Покрытие ролей'},
    {key: 'duplicatesScore', label: 'Без дубликатов'},
    // R12: «Полнота данных» обещала полноту всех свойств, а означала только цену.
    {key: 'completenessScore', label: 'Цена указана'},
];

const NOT_COMPUTED_LABELS: Record<string, string> = {
    INSUFFICIENT_HISTORY: 'Пока мало данных',
    UNINDEXED: 'Дубли не проверены',
};

// F11: при недостатке данных вместо идеального балла показывается честная подпись.
const NOT_ENOUGH_DATA = 'Пока мало данных';

const DEFAULT_FORMULA = 'Здоровье — четыре подметрики про порядок в гардеробе. '
    + 'Индекс гардероба в аналитике — другая метрика из шести подметрик.';

/**
 * Следующий шаг карточки: одна кнопка по коду совета с бэкенда (R12 — вместо трёх абзацев
 * объяснений). Код `NONE` и неизвестные коды кнопки не рисуют.
 */
const NEXT_STEP: Record<string, {label: string; route: string}> = {
    MARK_WEARS: {label: 'Отмечать носку', route: '/clothing'},
    WEAR_STALE: {label: 'Разобрать залежавшееся', route: '/disposal'},
    FILL_GAPS: {label: 'Посмотреть пробелы', route: '/insights'},
    REVIEW_DUPLICATES: {label: 'Разобрать дубликаты', route: '/insights'},
    FINISH_INDEXING: {label: 'Завершить индексацию', route: '/indexing'},
    FILL_PRICES: {label: 'Заполнить цены', route: '/clothing'},
    ADD_ITEMS: {label: 'Добавить вещи', route: '/clothing'},
};

/**
 * Самостоятельная карточка «Здоровье гардероба»: видимая часть — оценка, строка состояния и
 * один следующий шаг; состав метрик, веса и отличия от соседних оценок — в раскрываемом блоке
 * «Как считается» (R12). Грузит данные сама (для выбранного гардероба); некритичная — при
 * ошибке не отображается. Подметрика без данных показывается как «—», а не как 100.
 */
const WardrobeHealthCard: React.FC = () => {
    const {selectedOwnerId} = useWardrobe();
    const navigate = useNavigate();
    const [data, setData] = useState<WardrobeHealth | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const api = await createAxiosInstance();
            // ownerId — чтобы карточка описывала выбранный гардероб, как и остальные блоки главной.
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            const res = await api.get<WardrobeHealth>('/wardrobe-health', {params});
            setData(res.data);
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [selectedOwnerId]);

    useEffect(() => {
        load();
    }, [load]);

    if (loading) {
        return (
            <div className="card p-6 animate-pulse" data-testid="health-loading">
                <div className="h-5 w-48 bg-gray-200 dark:bg-ink-700 rounded mb-4"/>
                <div className="h-16 bg-gray-100 dark:bg-ink-800 rounded-xl"/>
            </div>
        );
    }

    if (error || !data) {
        return null;
    }

    if (data.consentRequired) {
        return (
            <div className="card p-6 space-y-3" data-testid="health-consent-required">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Здоровье гардероба</h2>
                <p className="text-gray-600 dark:text-gray-300">
                    Оценка выключена: вы отозвали согласие на профилирование поведения.
                </p>
                <Link
                    to="/profile"
                    className="inline-flex min-h-[44px] items-center px-4 rounded-xl bg-brand-600 text-white hover:bg-brand-700"
                    data-testid="health-consent-link"
                >
                    Включить в настройках приватности
                </Link>
            </div>
        );
    }

    const componentByKey = new Map((data.components ?? []).map(c => [c.key, c]));
    const KEY_MAP: Record<string, string> = {
        utilizationScore: 'utilization', coverageScore: 'coverage',
        duplicatesScore: 'duplicates', completenessScore: 'completeness',
    };

    /**
     * Q7: «100/100» складывалось из 40 % веса — утилизация и дубли не посчитаны. Это не оценка,
     * а приветствие. Формулу не меняем, меняем подачу: число вторичным цветом и подпись
     * «предварительно», пока хотя бы одна из двух тяжёлых подметрик без данных.
     */
    const preliminary = data.utilizationScore == null || data.duplicatesScore == null;
    // F11: когда обе тяжёлые подметрики без данных, балл не оценка, а приветствие — числа нет.
    const notEnoughData = data.utilizationScore == null && data.duplicatesScore == null;
    const nextStep = NEXT_STEP[data.topSuggestionCode ?? ''];

    return (
        <div className="card p-6 space-y-4" data-testid="wardrobe-health">
            <div className="flex items-center justify-between gap-3">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Здоровье гардероба</h2>
                <div className="text-right">
                    <div className={`text-3xl font-bold ${preliminary
                        ? 'text-gray-500 dark:text-gray-400' : scoreColor(data.score)}`}
                        data-testid="health-score">
                        {notEnoughData
                            ? <span className="text-base font-medium">{NOT_ENOUGH_DATA}</span>
                            : <>{data.score}<span className="text-base text-gray-400">/100</span></>}
                    </div>
                    {preliminary && (
                        <div className="text-xs text-gray-400 dark:text-gray-500" data-testid="health-preliminary">
                            предварительно
                        </div>
                    )}
                </div>
            </div>

            {preliminary && (
                <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="health-preliminary-hint">
                    {notEnoughData
                        ? 'Использование гардероба и дубли ещё не посчитаны — оценивать здоровье пока не по чему.'
                        : 'Часть подметрик ещё не посчитана, поэтому оценка неполная: она сложилась из того, что известно сейчас.'}
                </p>
            )}

            {/* Один следующий шаг вместо полотна объяснений: текст совета и кнопка к нему. */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <p className="flex-1 flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300"
                    data-testid="health-suggestion">
                    <LightbulbIcon className="w-4 h-4 mt-0.5 shrink-0 text-amber-500"/>
                    <span>{data.topSuggestion}</span>
                </p>
                {nextStep && (
                    <button
                        type="button"
                        onClick={() => navigate(nextStep.route)}
                        data-testid="health-next-step"
                        className="min-h-[44px] shrink-0 px-4 rounded-xl text-sm font-semibold
                            bg-brand-600 text-white hover:bg-brand-700 pressable
                            focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                    >
                        {nextStep.label}
                    </button>
                )}
            </div>

            {/* R12: состав метрик, веса и отличия от соседних оценок — по раскрытию, а не всегда. */}
            <details className="group border-t border-gray-100 dark:border-white/5 pt-3"
                data-testid="health-how">
                <summary className="flex min-h-[44px] cursor-pointer list-none items-center gap-1.5 text-sm
                    text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 rounded-lg">
                    <ChevronDownIcon className="w-4 h-4 transition-transform duration-200 group-open:rotate-180"/>
                    Как считается
                </summary>

                <div className="mt-3 space-y-3">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="health-subscores">
                        {SUBS.map(s => {
                            const value = data[s.key] as number | null;
                            const component = componentByKey.get(KEY_MAP[s.key]);
                            const reason = component?.notComputedReason ?? undefined;
                            return (
                                <div key={s.key} className="rounded-xl border border-gray-200 dark:border-ink-700 p-3"
                                    title={component?.hint ?? undefined} data-testid={`health-sub-${KEY_MAP[s.key]}`}>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {component?.labelRu ?? s.label}
                                        <span className="text-gray-400 dark:text-gray-500"> · {component?.weight ?? 0} %</span>
                                    </div>
                                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                        {value == null ? '—' : value}
                                    </div>
                                    {value == null && (
                                        <div className="text-[11px] text-gray-400 dark:text-gray-500"
                                            data-testid={`health-not-computed-${KEY_MAP[s.key]}`}>
                                            {NOT_COMPUTED_LABELS[reason ?? ''] ?? 'Пока не посчитано'}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* R12: «Полнота данных» обещала полноту всех свойств — говорим, что именно указано. */}
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="health-price-share">
                        Цена указана у {data.completenessScore} % вещей — другие свойства в эту подметрику не входят.
                    </p>

                    {/* F11: соответствие капсуле — отдельная метрика стратегии, к здоровью отношения не имеет. */}
                    <p className="text-xs text-gray-400 dark:text-gray-500" data-testid="health-vs-adherence">
                        Соответствие капсуле показывается отдельно на карточке «Сегодня»: это про выбранную
                        стратегию, а не про порядок в гардеробе.
                    </p>

                    {/* Здоровье и индекс — две метрики; разница объясняется здесь, а не угадывается. */}
                    <p className="text-xs text-gray-400 dark:text-gray-500" data-testid="health-formula">
                        {data.formulaLabel ?? DEFAULT_FORMULA}{' '}
                        <Link to="/insights" className="text-brand-600 dark:text-brand-400 hover:underline">
                            Открыть индекс в аналитике
                        </Link>
                    </p>
                </div>
            </details>
        </div>
    );
};

export default WardrobeHealthCard;
